import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  Heading,
  Input,
  Text,
  Image,
  Box,
  InjectedModalProps,
} from '@pancakeswap/uikit'
import styled from 'styled-components'
import { FixedSizeList } from 'react-window'
import {
  addCustomTokens,
  DEFAULT_XRP_ICON,
  getTokenKey,
  getTokenLabel,
  searchBithompTokens,
  useXrplTokens,
  XrplCurrency,
} from '../../xrpl'

const StyledModalContainer = styled(ModalContainer)`
  max-width: 420px;
  width: 100%;
`

const StyledModalBody = styled(ModalBody)`
  padding: 24px;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const TokenIcon = styled(Image)`
  border-radius: 50%;
`

const MenuItem = styled.div<{ disabled: boolean; selected: boolean }>`
  padding: 4px 20px;
  height: 72px;
  display: grid;
  grid-template-columns: auto minmax(auto, 1fr) minmax(0, 120px);
  grid-gap: 8px;
  align-items: center;
  cursor: ${({ disabled }) => (!disabled ? 'pointer' : 'default')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  :hover {
    background-color: ${({ theme, disabled }) => !disabled && theme.colors.background};
  }
  opacity: ${({ disabled, selected }) => (disabled || selected ? 0.5 : 1)};
`

const TokenMeta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const RightMeta = styled(Text)`
  text-align: right;
`

const fallbackIcon = '/images/cake.svg'

const shortIssuer = (issuer?: string) => {
  if (!issuer) return ''
  return `${issuer.slice(0, 4)}...${issuer.slice(-4)}`
}

interface XrplCurrencySearchModalProps extends InjectedModalProps {
  selectedCurrency?: XrplCurrency | null
  onCurrencySelect: (currency: XrplCurrency) => void
  otherSelectedCurrency?: XrplCurrency | null
}

export default function XrplCurrencySearchModal({
  onDismiss = () => null,
  onCurrencySelect,
  selectedCurrency,
  otherSelectedCurrency,
}: XrplCurrencySearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const { tokens, error: tokensError } = useXrplTokens()
  const fixedList = useRef<FixedSizeList>()

  const [remoteTokens, setRemoteTokens] = useState<XrplCurrency[]>([])
  const [remoteLoading, setRemoteLoading] = useState(false)
  const [remoteError, setRemoteError] = useState<string | null>(null)
  const trimmedQuery = searchQuery.trim()
  const normalizedQuery = trimmedQuery.toLowerCase()
  const shouldRemoteSearch = trimmedQuery.length >= 2

  useEffect(() => {
    let isMounted = true
    if (!shouldRemoteSearch) {
      setRemoteTokens([])
      setRemoteError(null)
      setRemoteLoading(false)
      return () => {
        isMounted = false
      }
    }

    setRemoteLoading(true)
    const timer = setTimeout(async () => {
      try {
        const results = await searchBithompTokens(trimmedQuery)
        if (isMounted) {
          setRemoteTokens(results)
          setRemoteError(null)
        }
      } catch (searchError) {
        if (isMounted) {
          setRemoteTokens([])
          setRemoteError(searchError.message || 'Token search failed')
        }
      } finally {
        if (isMounted) {
          setRemoteLoading(false)
        }
      }
    }, 300)

    return () => {
      isMounted = false
      clearTimeout(timer)
    }
  }, [trimmedQuery, shouldRemoteSearch])

  const mergedTokens = useMemo(() => {
    if (!remoteTokens.length) return tokens
    const ordered = new Map<string, XrplCurrency>()
    tokens.forEach((token) => ordered.set(getTokenKey(token), token))
    remoteTokens.forEach((token) => ordered.set(getTokenKey(token), token))
    return Array.from(ordered.values())
  }, [remoteTokens, tokens])

  const filteredTokens = useMemo(() => {
    if (!normalizedQuery) return mergedTokens
    return mergedTokens.filter((token) => {
      const haystack = [token.symbol, token.currency, token.name, token.issuer]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(normalizedQuery)
    })
  }, [mergedTokens, normalizedQuery])

  const handleSelect = useCallback(
    (currency: XrplCurrency) => {
      addCustomTokens([currency])
      onDismiss()
      onCurrencySelect(currency)
    },
    [onDismiss, onCurrencySelect],
  )

  const Row = useCallback(
    ({ data, index, style }) => {
      const token = data[index]
      if (!token) return null
      const isSelected = Boolean(selectedCurrency && getTokenKey(selectedCurrency) === getTokenKey(token))
      const isOtherSelected = Boolean(otherSelectedCurrency && getTokenKey(otherSelectedCurrency) === getTokenKey(token))
      const issuerLabel = token.isNative || token.currency === 'XRP' ? 'Native' : shortIssuer(token.issuer)

      return (
        <MenuItem
          style={style}
          disabled={isSelected}
          selected={isOtherSelected}
          onClick={() => (isSelected ? null : handleSelect(token))}
        >
          <TokenIcon
            src={token.icon || (token.isNative || token.currency === 'XRP' ? DEFAULT_XRP_ICON : fallbackIcon)}
            width={24}
            height={24}
            alt={token.symbol}
          />
          <TokenMeta>
            <Text bold>{token.symbol || getTokenLabel(token)}</Text>
            <Text color="textSubtle" small ellipsis>
              {token.name || token.currency}
            </Text>
          </TokenMeta>
          <RightMeta color="textSubtle" small>
            {issuerLabel}
          </RightMeta>
        </MenuItem>
      )
    },
    [handleSelect, otherSelectedCurrency, selectedCurrency],
  )

  return (
    <StyledModalContainer minWidth="320px">
      <ModalHeader>
        <ModalTitle>
          <Heading>Select a Token</Heading>
        </ModalTitle>
        <ModalCloseButton onDismiss={onDismiss} />
      </ModalHeader>
      <StyledModalBody>
        <Box>
          <Input
            id="token-search-input"
            placeholder="Search name or issuer"
            scale="lg"
            autoComplete="off"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value)
              fixedList.current?.scrollTo(0)
            }}
          />
          {tokensError && (
            <Text color="failure" fontSize="12px" mt="6px">
              {tokensError}
            </Text>
          )}
          {remoteLoading && (
            <Text color="textSubtle" fontSize="12px" mt="6px">
              Searching Bithomp...
            </Text>
          )}
          {remoteError && (
            <Text color="failure" fontSize="12px" mt="6px">
              {remoteError}
            </Text>
          )}
        </Box>
        {filteredTokens.length ? (
          <Box margin="24px -24px">
            <FixedSizeList
              height={390}
              itemData={filteredTokens}
              itemCount={filteredTokens.length}
              itemSize={72}
              width="100%"
              ref={fixedList}
            >
              {Row}
            </FixedSizeList>
          </Box>
        ) : (
          <Box padding="20px">
            <Text color="textSubtle" textAlign="center" mb="20px">
              No results found.
            </Text>
          </Box>
        )}
      </StyledModalBody>
    </StyledModalContainer>
  )
}
